import React from 'react';
import clsx from 'clsx';
import { alpha, Chip, useTheme, type Theme } from '@mui/material';

import Text from './Text';

export interface SquareChipProps {
	/** The component's css class name */
	className?: string;
	/** The chip label */
	label?: React.ReactNode;
	/** The chip background color */
	backgroundColor?: string | ((theme: Theme) => string | undefined);
	/** The chip icon */
	icon?: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
	/** Sets if the chip should be disabled */
	disabled?: boolean;
	/** Function called when the chip is clicked */
	onClick?: React.MouseEventHandler<HTMLDivElement>;
}

/** A component used to render a squared chip */
function SquareChip({ className, label, backgroundColor, icon, disabled, onClick }: SquareChipProps) {
	const theme = useTheme();

	const backgroundColorRgb = typeof backgroundColor === 'function' ? backgroundColor(theme) : backgroundColor;
	const hoverBackgroundColorRgb = backgroundColorRgb && !!onClick ? alpha(backgroundColorRgb, 0.4) : undefined;

	return (
		<Chip
			className={clsx('m-2 max-w-full rounded-4', className)}
			sx={{
				backgroundColor: backgroundColorRgb,
				color: backgroundColorRgb && theme.palette.getContrastText(backgroundColorRgb),
				'&:hover': {
					backgroundColor: hoverBackgroundColorRgb,
					color: hoverBackgroundColorRgb && theme.palette.getContrastText(hoverBackgroundColorRgb)
				}
			}}
			label={<Text className="text-12">{label}</Text>}
			icon={icon}
			size="small"
			disabled={disabled}
			onClick={onClick}
		/>
	);
}

export default React.memo(SquareChip);
